import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import FieldInput from 'brastrap/common/field-input/FieldInput';
import FieldSet from 'brastrap/common/fieldset/Fieldset';
import Button from 'brastrap/common/button/Button';
import Icon from 'brastrap/common/icon/Icon';
import Message from 'brastrap/common/message/Message';
import { defineMessages, injectIntl, intlShape } from 'react-intl';
import { applyModifiers } from '../../utils';

/**
 * Find items for return
 */

const messages = defineMessages({
  orderNoInfo: {
    id: 'returns.order-number-info',
    defaultMessage: 'Your 8-digit order number can be found in your confirmation email or on the Order Summary paperwork in your parcel. Please DO NOT include any slashes or subsequent numbers, such as /01. Only type in the first 8 digits of the number.',
  },
  zipCodeInfo: {
    id: 'returns.zip-code-info',
    defaultMessage: 'Please enter the 5-digit zip code of the delivery address your order was shipped to.',
  },
});

const ReturnsOrderSearch = (props: Props) => {
  const [zipcode, setZipcode] = useState(null);
  const [orderNo, setOrderNo] = useState(null);
  const [orderMessage, setOrderMessage] = useState(false);
  const [zipcodeMessage, setZipcodeMessage] = useState(false);
  const [displayErrorMessage, setDisplayErrorMessage] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isDismissable, setIsDismissable] = useState(false);

  const {
    intl: { formatMessage },
    invalidOrderErrorMsg,
    setInvalidOrderErrorMsg,
    isLoading,
    onOrderSearch,
  } = props;

  useEffect(() => {
    if (zipcode && orderNo) {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [zipcode, orderNo]);

  useEffect(() => {
    if (invalidOrderErrorMsg) {
      setDisplayErrorMessage(true);
      setIsDismissable(true);
      setIsButtonDisabled(true);
    }
  }, [invalidOrderErrorMsg]);

  const withSpinner = label => (
    <span id="spinner">
      <progress className={applyModifiers('c-activity', 'white small')} />{' '}
      {label}
    </span>
  );

  /**
   * Returns label or spinner
   * @returns {string}
   */
  function getLabel() {
    let label;
    if (isLoading) {
      label = withSpinner('Loading...');
    } else {
      label = 'Find My Order';
    }
    return label;
  }

  /**
   * Searches for an order based on Zipcode and Formatted Order Number
   */
  function handleOnClick() {
    setDisplayErrorMessage(false);
    setInvalidOrderErrorMsg(null);
    const formattedOrderNumber = orderNo.split('/')[0];
    onOrderSearch(zipcode, formattedOrderNumber);
  }

  return (
    <div className="c-returns-search">
      <div className="c-returns-search__form">
        <FieldSet modifiers={['no-margin']}>
          <div className="c-returns-search__inputSet">
            <FieldInput
              label="Order Number:"
              placeholder="Order Number"
              required
              id="order-number"
              type="text"
              modifiers={['medium']}
              value={orderNo}
              onChange={e => {
                setOrderNo(e.target.value);
              }}
            />
            <button
              className="c-returns-search__infoButton"
              onClick={() => {
                setOrderMessage(!orderMessage);
              }}
            >
              <Icon icon="information" />
            </button>
          </div>
          {orderMessage && (
            <div
              id="orderNumberInfoMessage"
              className="c-returns-search__informationMessage"
            >
              <Message
                dismissable
                onClose={() => setOrderMessage(false)}
                modifiers={['inform', 'banner']}
              >
                {formatMessage(messages.orderNoInfo)}
              </Message>
            </div>
          )}
        </FieldSet>

        <FieldSet modifiers={['no-margin']}>
          <div className="c-returns-search__inputSet">
            <FieldInput
              label="Zip Code:"
              placeholder="Zip Code"
              required
              id="zip"
              type="text"
              modifiers={['medium']}
              value={zipcode}
              onChange={e => {
                setZipcode(e.target.value);
              }}
            />
            <button
              className="c-returns-search__infoButton"
              onClick={() => {
                setZipcodeMessage(!zipcodeMessage);
              }}
            >
              <Icon icon="information" />
            </button>
          </div>
          {zipcodeMessage && (
            <div
              id="zipCodeInfoMessage"
              className="c-returns-search__informationMessage"
            >
              <Message
                dismissable
                onClose={() => setZipcodeMessage(false)}
                modifiers={['inform', 'banner']}
              >
                {formatMessage(messages.zipCodeInfo)}
              </Message>
            </div>
          )}
        </FieldSet>
        {displayErrorMessage && (
          <div className="c-returns-search__errorMessageContainer">
            <Message
              type="error"
              dismissable={isDismissable}
              onClose={() => setDisplayErrorMessage(false)}
              modifiers={['warn', 'banner']}
            >
              {invalidOrderErrorMsg}
            </Message>
          </div>
        )}
        <div className="c-returns-search__buttonContainer">
          <Button
            id="searchForOrder"
            label={getLabel()}
            modifiers={['full-width-mobile', 'default-side-padded']}
            onClick={() => {
              handleOnClick();
            }}
            disabled={isButtonDisabled}
          />
        </div>
      </div>
    </div>
  );
};

ReturnsOrderSearch.propTypes = {
  intl: intlShape,
  onOrderSearch: PropTypes.func,
};

ReturnsOrderSearch.defaultProps = {};

export default injectIntl(ReturnsOrderSearch);
